'use client'

/**
 * AnomaliesView — самодостаточный компонент вкладки «Аномалии» ML-аналитики:
 * фильтр сценария + витрина аномалий.
 *
 * ЕДИНЫЙ источник UI аномалий: рендерится на /cases (CasesPage, сегмент
 * «Аномалии ИИ»). Любые изменения здесь автоматически видны везде, где он
 * подключён.
 *
 * ДВА СЛОЯ ДАННЫХ (переключатель `source`):
 *  - 'new'    — case-level витрина новой модели `anomaly_v1` (сценарии 1–5):
 *               четырёхуровневая шкала аномальности, когорты сравнения,
 *               причины нетипичности, drawer с разбором кейса. Слой, который
 *               мы переносим с localhost:3009.
 *  - 'legacy' — прежняя pair-level витрина (v2): сводные карточки + таблица
 *               пар + drawer-объяснение. Остаётся как путь отката.
 *
 * Дефолт задан константой DEFAULT_SOURCE ниже и мгновенно переопределяется
 * через `?source=new` / `?source=legacy` в URL — без редеплоя. Если в новом
 * слое обнаружится проблема, `?source=legacy` возвращает прежнюю витрину
 * немедленно, а откат дефолта — правка одной строки.
 *
 * Экспортируется через публичный index.ts модуля analytics.
 */

import { useState, useEffect } from 'react'
import { useT } from '@/lib/i18n'
import type { DateRangeValue } from '@/components/ui/date-range-picker'
import { DataSelect } from '@/components/ui/select'
import { AnomalySummaryCards } from './anomaly-summary-cards'
import { AnomalyTable } from './anomaly-table'
import { AnomalyDetailDrawer } from './anomaly-detail-drawer'
import { AnomalyCasesView } from './anomaly-cases-view'
import { CrossUnitCasesView } from '../cross-unit/components/cross-unit-cases-view'
import { isCrossUnitEnabledClient, isCrossUnitScenario } from '../cross-unit/cross-unit.flag'

type AnomaliesSource = 'new' | 'legacy'

const DEFAULT_SOURCE: AnomaliesSource = 'new'

// Официальный сценарий кейса = source_scenario_id (1..7). Это ЕДИНСТВЕННЫЙ сценарий
// кейса в UI: dropdown и колонка «Сценарий» фильтруют/показывают именно его.
/** Только идентификаторы: подпись строится через t() по номеру. */
const SCENARIO_IDS = [1, 2, 3, 4, 5, 6, 7] as const

export interface AnomaliesViewProps {
  /**
   * Период по дате раскредитации импорта. Приходит из того же дейтпикера, что
   * фильтрует реестр кейсов, — отдельного контрола заводить не стали: в режиме
   * «Аномалии ИИ» пикер остаётся на месте и продолжает работать.
   */
  dateRange?: DateRangeValue
}

export function AnomaliesView({ dateRange }: AnomaliesViewProps = {}) {
  const t = useT()
  // 'all' = без сценарного фильтра (дефолт). Иначе id ∈ 1..7 фильтрует по source_scenario_id (колонка «Сценарий»).
  const [scenarioId, setScenarioId] = useState<number | 'all'>('all')
  const [selectedCase, setSelectedCase] = useState<string | null>(null)
  // Фильтры вкладки «Аномалии» (summary cards + таблица должны быть синхронны)
  const [excludeEaeu, setExcludeEaeu] = useState(true)
  const [mlLevel, setMlLevel] = useState('all')
  const [hideEmptyRoute, setHideEmptyRoute] = useState(true)
  // A.Score level + поиск тоже подняты, чтобы summary cards совпадали с таблицей
  const [aScoreLevel, setAScoreLevel] = useState('')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)

  // Источник читается из URL один раз при первом рендере на клиенте — без
  // эффекта и каскада ререндеров (тот же приём, что на localhost:3009).
  const [source, setSource] = useState<AnomaliesSource>(DEFAULT_SOURCE)
  const [urlChecked, setUrlChecked] = useState(false)
  if (!urlChecked && typeof window !== 'undefined') {
    setUrlChecked(true)
    const p = new URLSearchParams(window.location.search).get('source')
    if (p === 'legacy' || p === 'new') setSource(p)
  }
  const isNew = source === 'new'

  // Слой v3 покрывает только сценарии 1–5 (same_unit). Сценарии 6–7 — cross-unit:
  // отдельный каталог со своими моделями, поэтому у них своя витрина
  // (CrossUnitCasesView). Выглядит и считается она так же, как 1–5, но величины
  // разных каталогов намеренно не сводятся в одну таблицу.
  const crossUnitEnabled = isCrossUnitEnabledClient()
  const showCrossUnit = isNew && crossUnitEnabled && isCrossUnitScenario(scenarioId)
  // Сценарий 6/7 при выключенном клиентском флаге трактуем как «все» — тот же
  // приём, что на localhost:3009: витрина 1–5 не должна молча притворяться,
  // что отфильтровала по недоступному сценарию.
  const effectiveScenarioId: number | 'all' =
    isNew && !crossUnitEnabled && isCrossUnitScenario(scenarioId) ? 'all' : scenarioId

  return (
    <div className="space-y-5">
      {/* Фильтр сценария */}
      <div className="flex items-center justify-end gap-3">
        <label className="text-[10px] font-medium uppercase tracking-[0.08em] text-fg-tertiary">
          {t('common.field.scenario')}
        </label>
        <DataSelect
          value={effectiveScenarioId}
          onChange={e => setScenarioId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          wrapperClassName="w-auto"
        >
          <option value="all">
            {isNew ? t('analytics.filter.allScenarios15') : t('analytics.filter.allScenarios')}
          </option>
          {SCENARIO_IDS.map(s => {
            // Сценарии 6–7 обслуживает cross-unit слой. Пометки «кандидат /
            // эксперимент» здесь больше нет: она ничего не говорила аналитику о
            // кейсе, а витрина 6–7 теперь устроена так же, как 1–5.
            const unsupported = isNew && !crossUnitEnabled && s > 5
            return (
              <option key={s} value={s} disabled={unsupported}>
                {t('analytics.scenario.n', { n: s })}
                {unsupported ? t('analytics.filter.unsupported') : ''}
              </option>
            )
          })}
        </DataSelect>
      </div>

      {showCrossUnit ? (
        <CrossUnitCasesView scenarioId={scenarioId === 6 ? 6 : 7} />
      ) : isNew ? (
        <AnomalyCasesView scenarioId={effectiveScenarioId} dateRange={dateRange} />
      ) : (
        <>
          <AnomalySummaryCards
            excludeEaeu={excludeEaeu}
            mlLevel={mlLevel}
            hideEmptyRoute={hideEmptyRoute}
            htLevel="all"
            aScoreLevel={aScoreLevel}
            search={debouncedSearch}
            scenarioId={scenarioId}
          />
          <AnomalyTable
            onSelectCase={setSelectedCase}
            excludeEaeu={excludeEaeu}
            onExcludeEaeuChange={setExcludeEaeu}
            mlLevel={mlLevel}
            onMlLevelChange={setMlLevel}
            hideEmptyRoute={hideEmptyRoute}
            onHideEmptyRouteChange={setHideEmptyRoute}
            aScoreLevel={aScoreLevel}
            onAScoreLevelChange={setAScoreLevel}
            search={search}
            onSearchChange={setSearch}
            debouncedSearch={debouncedSearch}
            scenarioId={scenarioId}
          />

          <AnomalyDetailDrawer caseId={selectedCase} onClose={() => setSelectedCase(null)} />
        </>
      )}
    </div>
  )
}

function useDebounce(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}
